import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Switch } from "./ui/switch";
import {
  Rocket, Copy, ArrowSquareOut, Globe, Key, Plus, Trash, CircleNotch,
  CheckCircle, Circle, Eye, EyeSlash, ArrowClockwise,
} from "@phosphor-icons/react";

export default function DeployModal({ projectId, open, onOpenChange, onDeployed }) {
  const [project, setProject] = useState(null);
  const [deploying, setDeploying] = useState(false);
  const [domain, setDomain] = useState("");
  const [envs, setEnvs] = useState([]);
  const [savingDomain, setSavingDomain] = useState(false);
  const [savingEnv, setSavingEnv] = useState(false);
  const [revealed, setRevealed] = useState({});

  useEffect(() => {
    if (!open) return;
    api.get(`/api/projects/${projectId}`).then((r) => {
      setProject(r.data);
      setDomain(r.data.custom_domain || "");
      setEnvs(r.data.env_vars || []);
    });
  }, [open, projectId]);

  const publicUrl = project?.slug
    ? `${window.location.origin}/api/sites/${project.slug}`
    : null;
  const liveUrl = project?.custom_domain && project?.domain_verified
    ? `https://${project.custom_domain}`
    : publicUrl;

  const deploy = async () => {
    setDeploying(true);
    try {
      const r = await api.post(`/api/projects/${projectId}/deploy`);
      setProject(r.data);
      toast.success(project?.published ? "Redeployed successfully" : "App published!");
      onDeployed?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Deploy failed");
    } finally {
      setDeploying(false);
    }
  };

  const unpublish = async () => {
    try {
      await api.post(`/api/projects/${projectId}/unpublish`);
      setProject((p) => ({ ...p, published: false }));
      toast.success("App unpublished");
    } catch {
      toast.error("Failed to unpublish");
    }
  };

  const saveDomain = async () => {
    setSavingDomain(true);
    try {
      const r = await api.put(`/api/projects/${projectId}/settings`, { custom_domain: domain });
      setProject(r.data);
      toast.success("Domain saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save domain");
    } finally {
      setSavingDomain(false);
    }
  };

  const verifyDomain = async () => {
    try {
      await api.post(`/api/projects/${projectId}/verify-domain`);
      setProject((p) => ({ ...p, domain_verified: true }));
      toast.success("Domain connected");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    }
  };

  const saveEnvs = async () => {
    setSavingEnv(true);
    try {
      const clean = envs.filter((e) => e.key.trim());
      const r = await api.put(`/api/projects/${projectId}/settings`, { env_vars: clean });
      setProject(r.data);
      setEnvs(r.data.env_vars || []);
      toast.success("Environment variables saved");
    } catch {
      toast.error("Failed to save env vars");
    } finally {
      setSavingEnv(false);
    }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied to clipboard"); };
  const host = window.location.host;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-forge-surface border border-forge-border text-white max-w-xl rounded-sm p-0 gap-0" data-testid="deploy-modal">
        <DialogHeader className="p-5 border-b border-forge-border">
          <DialogTitle className="flex items-center gap-2 text-base font-medium tracking-tight">
            <Rocket size={18} weight="fill" className="text-forge-primary" /> Deploy — {project?.name}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="deploy" className="p-5">
          <TabsList className="bg-forge-bg border border-forge-border rounded-sm h-9 w-full grid grid-cols-3">
            <TabsTrigger value="deploy" data-testid="tab-deploy" className="rounded-sm data-[state=active]:bg-forge-primary data-[state=active]:text-black text-neutral-400 text-xs">Deploy</TabsTrigger>
            <TabsTrigger value="domain" data-testid="tab-domain" className="rounded-sm data-[state=active]:bg-forge-primary data-[state=active]:text-black text-neutral-400 text-xs">Domain</TabsTrigger>
            <TabsTrigger value="env" data-testid="tab-env" className="rounded-sm data-[state=active]:bg-forge-primary data-[state=active]:text-black text-neutral-400 text-xs">Secrets</TabsTrigger>
          </TabsList>

          {/* DEPLOY */}
          <TabsContent value="deploy" className="mt-5 space-y-4">
            <div className="flex items-center gap-2 text-sm">
              {project?.published ? (
                <><CheckCircle size={18} weight="fill" className="text-green-500" /><span className="text-green-400">Live</span></>
              ) : (
                <><Circle size={18} className="text-neutral-500" /><span className="text-neutral-400">Not deployed yet</span></>
              )}
            </div>

            {project?.published && liveUrl && (
              <div className="border border-forge-border bg-forge-bg rounded-sm p-3">
                <p className="uppercase text-[10px] tracking-[0.2em] text-neutral-500 mb-2">Live URL</p>
                <div className="flex items-center gap-2">
                  <a href={liveUrl} target="_blank" rel="noreferrer" data-testid="live-url"
                     className="flex-1 text-sm font-mono text-forge-primary truncate hover:underline">{liveUrl}</a>
                  <button onClick={() => copy(liveUrl)} className="p-1.5 text-neutral-400 hover:text-white transition-colors" title="Copy"><Copy size={15} /></button>
                  <a href={liveUrl} target="_blank" rel="noreferrer" className="p-1.5 text-neutral-400 hover:text-white transition-colors" title="Open"><ArrowSquareOut size={15} /></a>
                </div>
              </div>
            )}

            <button
              data-testid="deploy-btn"
              onClick={deploy}
              disabled={deploying}
              className="w-full flex items-center justify-center gap-2 bg-forge-primary text-black font-medium py-2.5 rounded-sm hover:bg-yellow-400 transition-colors disabled:opacity-60"
            >
              {deploying ? <CircleNotch size={16} className="animate-spin" /> : (project?.published ? <ArrowClockwise size={16} weight="bold" /> : <Rocket size={16} weight="fill" />)}
              {project?.published ? "Redeploy" : "Deploy now"}
            </button>
            {project?.published && (
              <button onClick={unpublish} data-testid="unpublish-btn" className="w-full text-xs text-neutral-500 hover:text-red-400 transition-colors">
                Unpublish app
              </button>
            )}
            <p className="text-xs text-neutral-500 leading-relaxed">
              Publishing serves your app live on a Matrix URL instantly. Redeploy anytime after making changes.
            </p>
          </TabsContent>

          {/* DOMAIN */}
          <TabsContent value="domain" className="mt-5 space-y-4">
            <div>
              <label className="uppercase text-[10px] tracking-[0.2em] text-neutral-400 mb-2 flex items-center gap-1.5"><Globe size={12} /> Custom domain</label>
              <div className="flex gap-2">
                <input
                  data-testid="domain-input"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="app.yourdomain.com"
                  className="flex-1 bg-forge-bg border border-forge-border px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-1 focus:ring-forge-primary focus:border-forge-primary"
                />
                <button onClick={saveDomain} disabled={savingDomain} data-testid="save-domain-btn"
                  className="px-4 bg-forge-hover border border-forge-border text-sm rounded-sm hover:border-forge-primary transition-colors disabled:opacity-60">
                  {savingDomain ? <CircleNotch size={14} className="animate-spin" /> : "Save"}
                </button>
              </div>
            </div>

            {project?.custom_domain && (
              <>
                <div className="border border-forge-border bg-forge-bg rounded-sm p-3 space-y-2">
                  <p className="uppercase text-[10px] tracking-[0.2em] text-neutral-500">DNS setup</p>
                  <p className="text-xs text-neutral-400">Add this CNAME record at your domain registrar, then click Connect:</p>
                  <div className="font-mono text-xs bg-black/40 border border-forge-border rounded-sm p-2 space-y-1">
                    <div className="flex justify-between"><span className="text-neutral-500">Type</span><span>CNAME</span></div>
                    <div className="flex justify-between"><span className="text-neutral-500">Name</span><span>{project.custom_domain}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-neutral-500">Value</span><span className="text-forge-primary truncate">{host}</span></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`flex items-center gap-1.5 text-sm ${project.domain_verified ? "text-green-400" : "text-neutral-400"}`}>
                    {project.domain_verified ? <><CheckCircle size={16} weight="fill" /> Connected — app auto-linked</> : <><Circle size={16} /> Pending connection</>}
                  </span>
                  {!project.domain_verified && (
                    <button onClick={verifyDomain} data-testid="verify-domain-btn"
                      className="text-sm bg-forge-primary text-black px-4 py-1.5 rounded-sm hover:bg-yellow-400 transition-colors">Connect</button>
                  )}
                </div>
              </>
            )}
            <p className="text-xs text-neutral-500 leading-relaxed">
              Once connected, your published app automatically serves from your custom domain. DNS can take 5–15 min to propagate.
            </p>
          </TabsContent>

          {/* ENV / SECRETS */}
          <TabsContent value="env" className="mt-5 space-y-3">
            <p className="text-xs text-neutral-400 flex items-center gap-1.5"><Key size={13} className="text-forge-primary" /> API keys, secrets & environment variables</p>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {envs.length === 0 && <p className="text-xs text-neutral-600 py-2">No variables yet. Add one below.</p>}
              {envs.map((e, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`env-row-${i}`}>
                  <input
                    value={e.key}
                    onChange={(ev) => setEnvs((arr) => arr.map((x, j) => j === i ? { ...x, key: ev.target.value } : x))}
                    placeholder="KEY"
                    className="w-2/5 bg-forge-bg border border-forge-border px-2 py-1.5 text-xs font-mono rounded-sm focus:outline-none focus:ring-1 focus:ring-forge-primary"
                  />
                  <div className="flex-1 relative">
                    <input
                      type={e.secret && !revealed[i] ? "password" : "text"}
                      value={e.value}
                      onChange={(ev) => setEnvs((arr) => arr.map((x, j) => j === i ? { ...x, value: ev.target.value } : x))}
                      placeholder="value"
                      className="w-full bg-forge-bg border border-forge-border px-2 py-1.5 pr-8 text-xs font-mono rounded-sm focus:outline-none focus:ring-1 focus:ring-forge-primary"
                    />
                    {e.secret && (
                      <button onClick={() => setRevealed((r) => ({ ...r, [i]: !r[i] }))} className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white">
                        {revealed[i] ? <EyeSlash size={13} /> : <Eye size={13} />}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1" title="Mark as secret">
                    <Switch checked={!!e.secret} onCheckedChange={(v) => setEnvs((arr) => arr.map((x, j) => j === i ? { ...x, secret: v } : x))} />
                  </div>
                  <button onClick={() => setEnvs((arr) => arr.filter((_, j) => j !== i))} className="text-neutral-500 hover:text-red-400 transition-colors"><Trash size={14} /></button>
                </div>
              ))}
            </div>
            <button
              onClick={() => setEnvs((arr) => [...arr, { key: "", value: "", secret: false }])}
              data-testid="add-env-btn"
              className="flex items-center gap-1.5 text-xs text-forge-primary hover:text-yellow-300 transition-colors"
            >
              <Plus size={14} /> Add variable
            </button>
            <button
              onClick={saveEnvs}
              disabled={savingEnv}
              data-testid="save-env-btn"
              className="w-full flex items-center justify-center gap-2 bg-forge-primary text-black font-medium py-2 rounded-sm hover:bg-yellow-400 transition-colors disabled:opacity-60 mt-1"
            >
              {savingEnv ? <CircleNotch size={15} className="animate-spin" /> : <Key size={15} />} Save variables
            </button>
            <p className="text-[11px] text-neutral-600 leading-relaxed">
              Non-secret vars are injected into your published app as <span className="font-mono">window.MATRIX_ENV</span>. Secret vars are stored securely and never exposed to the client.
            </p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
